import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store';
import { Loading } from '@/components';
import { RoutesMap } from './RoutesMap';

// Lazy loaded screens
const Splash = lazy(() => import('@/screens/Splash'));
const SelectRole = lazy(() => import('@/screens/Auth/SelectRole'));
const BarberType = lazy(() => import('@/screens/Auth/BarberType'));

// Owner auth
const Login = lazy(() => import('@/screens/Auth/Login'));
const Register = lazy(() => import('@/screens/Auth/Register'));
const OwnerForgotPassword = lazy(() => import('@/screens/Auth/ForgotPassword'));

// Team member auth
const TeamMemberLogin = lazy(() => import('@/screens/Auth/TeamMember/Login'));
const TeamMemberCreatePassword = lazy(() => import('@/screens/Auth/TeamMember/CreatePassword'));
const TeamMemberForgotPassword = lazy(() => import('@/screens/Auth/ForgotPassword'));

// Customer auth
const CustomerRegister = lazy(() => import('@/screens/Auth/Customer/Register'));
const CustomerLogin = lazy(() => import('@/screens/Auth/Customer/Login'));
const CustomerForgotPassword = lazy(() => import('@/screens/Auth/ForgotPassword'));

// Barber private screens
const Layout = lazy(() => import('@/screens/Layout'));
const Dashboard = lazy(() => import('@/screens/Dashboard'));
const Appointments = lazy(() => import('@/screens/Appointments'));
const Earnings = lazy(() => import('@/screens/Earnings'));
const Championship = lazy(() => import('@/screens/Championship'));
const EmployeeBarbersRanking = lazy(() => import('@/screens/EmployeeBarbersRanking'));
const Targets = lazy(() => import('@/screens/Targets'));
const News = lazy(() => import('@/screens/News'));
const AboutUs = lazy(() => import('@/screens/AboutUs'));
const ContactUs = lazy(() => import('@/screens/ContactUs'));
const YourTeam = lazy(() => import('@/screens/Employees'));
const Profile = lazy(() => import('@/screens/Profile'));
const AddMember = lazy(() => import('@/screens/Employees/AddMember'));
const Analytics = lazy(() => import('@/screens/Analytics'));
const Ratings = lazy(() => import('@/screens/Ratings'));
const StaffMessages = lazy(() => import('@/screens/Messages'));
const ShopSchedule = lazy(() => import('@/screens/ShopSchedule'));

// Customer private screens
const CustomerLayout = lazy(() => import('@/screens/CustomerLayout'));
const CustomerHome = lazy(() => import('@/screens/Customer/Home'));
const CustomerAppointments = lazy(() => import('@/screens/Customer/Appointments'));
const CustomerBarberRank = lazy(() => import('@/screens/Customer/BarberRank'));
const CustomerBarbershopsRanks = lazy(() => import('@/screens/Customer/BarbershopsRanks'));
const CustomerFavourites = lazy(() => import('@/screens/Customer/Favourites'));
const ShopBarbers = lazy(() => import('@/screens/Customer/ShopBarbers'));
const CustomerMessages = lazy(() => import('@/screens/Customer/Messages'));
const CustomerProfile = lazy(() => import('@/screens/Customer/Profile'));
const CustomerAboutUs = lazy(() => import('@/screens/Customer/AboutUs'));
const CustomerContactUs = lazy(() => import('@/screens/Customer/ContactUs'));
const BarberProfile = lazy(() => import('@/screens/Customer/BarberProfile'));
const BookingFlow = lazy(() => import('@/screens/Customer/BookingFlow'));
const BookingConfirm = lazy(() => import('@/screens/Customer/BookingConfirm'));
const PaymentCancelled = lazy(() => import('@/screens/Customer/PaymentCancelled'));
const PublicBarberPage = lazy(() => import('@/screens/PublicBarberPage'));
const PrivacyPolicy = lazy(() => import('@/screens/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@/screens/TermsOfService'));

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}

/** Barber (owner/employee) private route */
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const userType = useAuthStore((s) => s.userType);
  if (!isLoggedIn) return <Navigate to={RoutesMap.BARBER_TYPE} replace />;
  if (userType === 'customer') return <Navigate to={RoutesMap.CUSTOMER_HOME} replace />;
  return <>{children}</>;
}

/** Guest or customer route — allows unauthenticated guests AND logged-in customers */
function GuestOrCustomerRoute({ children }: { children: React.ReactNode }) {
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const userType = useAuthStore((s) => s.userType);
  if (isLoggedIn && userType !== 'customer') return <Navigate to={RoutesMap.DASHBOARD} replace />;
  return <>{children}</>;
}

/** Barber auth public route */
function PublicRoute({ children }: { children: React.ReactNode }) {
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const userType = useAuthStore((s) => s.userType);
  if (isLoggedIn && userType === 'customer')
    return <Navigate to={RoutesMap.CUSTOMER_HOME} replace />;
  if (isLoggedIn) return <Navigate to={RoutesMap.DASHBOARD} replace />;
  return <>{children}</>;
}

/** Customer auth public route */
function CustomerPublicRoute({ children }: { children: React.ReactNode }) {
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const userType = useAuthStore((s) => s.userType);
  if (isLoggedIn && userType === 'customer')
    return <Navigate to={RoutesMap.CUSTOMER_HOME} replace />;
  return <>{children}</>;
}

/** Only accessible when logged in with a temporary password still pending reset */
function TempPasswordRoute({ children }: { children: React.ReactNode }) {
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const isPasswordTemporary = useAuthStore((s) => s.isPasswordTemporary);
  if (!isLoggedIn) return <Navigate to={RoutesMap.TEAM_MEMBER_LOGIN} replace />;
  if (!isPasswordTemporary) return <Navigate to={RoutesMap.DASHBOARD} replace />;
  return <>{children}</>;
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <SuspenseWrapper>
        <Routes>
          {/* Splash - always show, no auth check */}
          <Route path={RoutesMap.SPLASH} element={<Splash />} />

          {/* Role selection - no auth check */}
          <Route path={RoutesMap.SELECT_ROLE} element={<SelectRole />} />
          <Route path={RoutesMap.BARBER_TYPE} element={<BarberType />} />

          {/* Customer auth */}
          <Route
            path={RoutesMap.CUSTOMER_REGISTER}
            element={
              <CustomerPublicRoute>
                <CustomerRegister />
              </CustomerPublicRoute>
            }
          />
          <Route
            path={RoutesMap.CUSTOMER_LOGIN}
            element={
              <CustomerPublicRoute>
                <CustomerLogin />
              </CustomerPublicRoute>
            }
          />
          <Route
            path={RoutesMap.CUSTOMER_FORGOT_PASSWORD}
            element={
              <CustomerPublicRoute>
                <CustomerForgotPassword />
              </CustomerPublicRoute>
            }
          />

          {/* Owner auth - standalone screens (redirect to dashboard if logged in) */}
          <Route
            path={RoutesMap.OWNER_LOGIN}
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path={RoutesMap.OWNER_REGISTER}
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route
            path={RoutesMap.OWNER_FORGOT_PASSWORD}
            element={
              <PublicRoute>
                <OwnerForgotPassword />
              </PublicRoute>
            }
          />

          {/* Team member auth */}
          <Route
            path={RoutesMap.TEAM_MEMBER_LOGIN}
            element={
              <PublicRoute>
                <TeamMemberLogin />
              </PublicRoute>
            }
          />
          <Route
            path={RoutesMap.TEAM_MEMBER_CREATE_PASSWORD}
            element={
              <TempPasswordRoute>
                <TeamMemberCreatePassword />
              </TempPasswordRoute>
            }
          />
          <Route
            path={RoutesMap.TEAM_MEMBER_FORGOT_PASSWORD}
            element={
              <PublicRoute>
                <TeamMemberForgotPassword />
              </PublicRoute>
            }
          />

          {/* Customer Routes — guests + logged-in customers */}
          <Route
            element={
              <GuestOrCustomerRoute>
                <CustomerLayout />
              </GuestOrCustomerRoute>
            }
          >
            <Route path={RoutesMap.CUSTOMER_HOME} element={<CustomerHome />} />
            <Route path={RoutesMap.CUSTOMER_APPOINTMENTS} element={<CustomerAppointments />} />
            <Route path={RoutesMap.CUSTOMER_BARBER_RANK} element={<CustomerBarberRank />} />
            <Route
              path={RoutesMap.CUSTOMER_BARBERSHOPS_RANKS}
              element={<CustomerBarbershopsRanks />}
            />
            <Route path={RoutesMap.CUSTOMER_FAVOURITES} element={<CustomerFavourites />} />
            <Route path={RoutesMap.CUSTOMER_SHOP_BARBERS} element={<ShopBarbers />} />
            <Route path={RoutesMap.CUSTOMER_MESSAGES} element={<CustomerMessages />} />
            <Route path={RoutesMap.CUSTOMER_PROFILE} element={<CustomerProfile />} />
            <Route path={RoutesMap.CUSTOMER_ABOUT_US} element={<CustomerAboutUs />} />
            <Route path={RoutesMap.CUSTOMER_CONTACT_US} element={<CustomerContactUs />} />
          </Route>

          {/* Customer standalone pages (no sidebar) — guests allowed */}
          <Route
            path={RoutesMap.CUSTOMER_BARBER_PROFILE}
            element={
              <GuestOrCustomerRoute>
                <BarberProfile />
              </GuestOrCustomerRoute>
            }
          />
          <Route
            path={RoutesMap.CUSTOMER_BARBER_BOOK}
            element={
              <GuestOrCustomerRoute>
                <BookingFlow />
              </GuestOrCustomerRoute>
            }
          />
          <Route
            path={RoutesMap.CUSTOMER_BOOKING_CONFIRM}
            element={
              <GuestOrCustomerRoute>
                <BookingConfirm />
              </GuestOrCustomerRoute>
            }
          />
          <Route
            path={RoutesMap.PAYMENT_CANCELLED}
            element={
              <SuspenseWrapper>
                <PaymentCancelled />
              </SuspenseWrapper>
            }
          />

          {/* Barber Private Routes - App */}
          <Route
            element={
              <PrivateRoute>
                <Layout />
              </PrivateRoute>
            }
          >
            <Route path={RoutesMap.DASHBOARD} element={<Dashboard />} />
            <Route path={RoutesMap.APPOINTMENTS} element={<Appointments />} />
            <Route path={RoutesMap.EARNINGS} element={<Earnings />} />
            <Route path={RoutesMap.CHAMPIONSHIP} element={<Championship />} />
            <Route path={RoutesMap.EMPLOYEE_BARBERS_RANKING} element={<EmployeeBarbersRanking />} />
            <Route path={RoutesMap.TARGETS} element={<Targets />} />
            <Route path={RoutesMap.NEWS} element={<News />} />
            <Route path={RoutesMap.ABOUT_US} element={<AboutUs />} />
            <Route path={RoutesMap.CONTACT_US} element={<ContactUs />} />
            <Route path={RoutesMap.YOUR_TEAM} element={<YourTeam />} />
            <Route path={RoutesMap.ADD_MEMBER} element={<AddMember />} />
            <Route path={RoutesMap.PROFILE} element={<Profile />} />
            <Route path={RoutesMap.ANALYTICS} element={<Analytics />} />
            <Route path={RoutesMap.RATINGS} element={<Ratings />} />
            <Route path={RoutesMap.MESSAGES} element={<StaffMessages />} />
            <Route path={RoutesMap.SHOP_SCHEDULE} element={<ShopSchedule />} />
          </Route>

          {/* Public barber booking page — no auth wrapper */}
          <Route path={RoutesMap.PUBLIC_BARBER} element={<PublicBarberPage />} />

          {/* Legal pages — fully public */}
          <Route path={RoutesMap.PRIVACY_POLICY} element={<PrivacyPolicy />} />
          <Route path={RoutesMap.TERMS_OF_SERVICE} element={<TermsOfService />} />

          {/* Catch all - back to splash */}
          <Route path="*" element={<Navigate to={RoutesMap.SPLASH} replace />} />
        </Routes>
      </SuspenseWrapper>
    </BrowserRouter>
  );
}
